import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Download, FileText, Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { PipelineRun } from "../../api/posthogReportsApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { useCurrentUser } from "@/auth/RequireAuth";
import { PageHeader } from "../../components/Common/PageHeader";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";
import { TagCoverage } from "./TagCoverage";

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

/** Raises an OS notification when the tab is not in the foreground.
 *
 *  A run takes minutes, so the in-page alert alone only reaches someone who
 *  sat and watched. Best-effort by design: the API is missing in insecure
 *  contexts and the permission may be denied or dismissed -- in every one of
 *  those cases the in-page alert is still the real signal, so we never
 *  surface a failure here. */
function notifyReportReady(body: string) {
  if (typeof Notification === "undefined") return;
  if (!document.hidden) return;
  if (Notification.permission !== "granted") return;
  new Notification("Event Data Analysis", { body });
}

/** Asks for notification permission while the click is still on the stack.
 *
 *  Must happen on the user gesture, not when the run finishes minutes later:
 *  Safari requires a gesture for requestPermission() and silently rejects it
 *  otherwise, which would make the first run never notify. */
function requestNotifyPermission() {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "default") return;
  void Notification.requestPermission().catch(() => {});
}

/** Runs the PostHog analytics pipeline and shows the report it produces.
 *
 *  The admin gate here is cosmetic — posthog_service checks OpenFGA on every
 *  request (app.require_admin), so hiding the button is a courtesy, not the
 *  boundary. */
export function EventAnalysis() {
  const { posthogReports } = useAuthedApi();
  const user = useCurrentUser();
  // Cosmetic only: posthog_service re-checks against OpenFGA on every
  // request (app.require_admin), which is the real boundary. `roles` here is
  // whatever /auth/me surfaced; an empty list just hides the button from
  // someone the server would have let through.
  const isAdmin =
    Boolean(user?.is_platform_admin) ||
    (user?.roles ?? []).some((role) => role === "admin" || role === "owner");

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [loadingReport, setLoadingReport] = useState(true);
  // Saved copies need distinguishable names; the blob itself carries none.
  const downloadName = `EventDataAnalysis_${new Date().toISOString().slice(0, 10)}.pdf`;

  // The blob URL is owned by this component; without revoking it, every
  // refresh leaks a copy of the PDF for the life of the tab.
  const urlRef = useRef<string | null>(null);
  const setReport = useCallback((next: string | null) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = next;
    setReportUrl(next);
  }, []);

  /** Loads the latest report; returns its URL, or null when there is none.
   *  The return value matters: a finished run is not the same as a run that
   *  produced a report, and only the caller can tell the difference. */
  const loadReport = useCallback(async (): Promise<string | null> => {
    setLoadingReport(true);
    setError(null);
    try {
      const url = await posthogReports.latestReportUrl();
      setReport(url);
      return url;
    } catch (e) {
      setError(message(e));
      setReport(null);
      return null;
    } finally {
      setLoadingReport(false);
    }
  }, [posthogReports, setReport]);

  useEffect(() => {
    if (isAdmin) void loadReport();
  }, [isAdmin, loadReport]);

  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  // Longest a run is allowed to hold the button before we stop watching. The
  // run itself is unaffected -- it keeps going server-side and "Refresh
  // report" still works; this only bounds the polling.
  const POLL_MS = 5_000;
  const POLL_TIMEOUT_MS = 20 * 60_000;

  const onRun = useCallback(async () => {
    requestNotifyPermission();
    setRunning(true);
    setError(null);
    setNotice(null);
    setProgress(null);

    let started;
    try {
      started = await posthogReports.runPipeline();
    } catch (e) {
      setError(message(e));
      setRunning(false);
      return;
    }

    // One run at a time server-side, so a second click joins the run already
    // in flight rather than starting another -- and polling it is just as
    // valid as polling one we started.
    setProgress(
      started.already_running
        ? "A run was already in progress — watching it."
        : "Running… sync → humanize → archive → map → report.",
    );

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    try {
      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
        if (Date.now() > deadline) {
          setNotice(
            "Still running after 20 minutes. It will finish on its own — use Refresh report later.",
          );
          return;
        }
        const run = await posthogReports.getRun(started.run_id);
        const steps: PipelineRun["steps"] = run.steps ?? {};
        if (run.status === "running") {
          const done = Object.keys(steps).length;
          setProgress(`Running… ${done} of 5 steps complete.`);
          continue;
        }
        // A failed run produced nothing. Observed live 2026-09-23: the pod was
        // OOMKilled mid-archive, the stranded row was later reaped to
        // "failed", and this loop announced "Report ready." on the next poll
        // -- treating every non-running status as success. Worse, the run had
        // recorded no steps at all, so the `failed` list below was empty and
        // the message carried no caveat either.
        if (run.status === "failed") {
          setError(
            run.error
              ? `The run failed: ${run.error}`
              : "The run failed before producing a report.",
          );
          return;
        }

        // "partial" is not failure: archive can die without stopping the
        // report step, so name the steps that broke rather than reporting a
        // blanket success or failure.
        const failed = Object.entries(steps)
          .filter(([, result]) => result?.status === "failed")
          .map(([name]) => name);

        // Finished is not the same as produced. Only the presence of a report
        // justifies saying one is ready.
        const url = await loadReport();
        if (!url) {
          setNotice(
            failed.length
              ? `The run finished but wrote no report (${failed.join(", ")} failed).`
              : "The run finished but wrote no report.",
          );
          return;
        }

        const summary = failed.length
          ? `Report ready. Some steps failed (${failed.join(", ")}) — the report may be incomplete.`
          : "Report ready.";
        setNotice(summary);
        notifyReportReady(summary);
        return;
      }
    } catch (e) {
      setError(message(e));
    } finally {
      setRunning(false);
      setProgress(null);
    }
  }, [posthogReports, loadReport]);

  if (!isAdmin) return null;

  return (
    <Box>
      <PageHeader
        title="Event Data Analysis"
        subtitle="Weekly onboarding health per company, from product usage"
      />

      <WorkspaceCard>
        {/* The tab's actions. The coverage card below keeps the
            `tag_coverage` section it shipped with, so its history holds. */}
        <Stack data-ph-scope="event_analysis" direction="row" spacing={1} sx={{ mb: 2, alignItems: "center" }}>
          <Button
            data-ph="create_report"
            variant="contained"
            startIcon={<Play size={16} />}
            onClick={onRun}
            disabled={running}
          >
            {running ? "Starting…" : "Run analysis"}
          </Button>
          <Button
            data-ph="refresh"
            variant="outlined"
            startIcon={<FileText size={16} />}
            onClick={loadReport}
            disabled={loadingReport || running}
          >
            Refresh report
          </Button>
          {/* Downloads the blob already in memory -- no second fetch, and no
              need for the private bucket to be publicly reachable. */}
          <Button
            data-ph="download"
            variant="outlined"
            startIcon={<Download size={16} />}
            component="a"
            href={reportUrl ?? undefined}
            download={downloadName}
            disabled={!reportUrl}
          >
            Download PDF
          </Button>
        </Stack>

        {progress && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "center" }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {progress}
            </Typography>
          </Stack>
        )}
        {notice && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loadingReport ? (
          <Typography variant="body2" color="text.secondary">
            Loading the latest report…
          </Typography>
        ) : reportUrl ? (
          <Box
            component="iframe"
            src={reportUrl}
            title="Event Data Analysis report"
            sx={{ width: "100%", height: "70vh", border: 0 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No report yet. Run the analysis to generate one.
          </Typography>
        )}
      </WorkspaceCard>

      {/* The coverage route is platform-admin only, unlike the report gate above. */}
      {user?.is_platform_admin && <TagCoverage />}
    </Box>
  );
}
